export type PolymerMode = 'addition' | 'condensation' | 'biomolecules' | 'soaps' | 'food-enzymes';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const POLYMERS_TOOL_IDENTITY = {
  id: 'polymers-biomolecules',
  toolKey: 'chemistry-polymers-biomolecules',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const POLYMER_MODE_ORDER: readonly PolymerMode[] = [
  'addition',
  'condensation',
  'biomolecules',
  'soaps',
  'food-enzymes',
];

/**
 * Year defaults for `modeForYear()`, ascending by start Year. Year 11 and no Year start with
 * addition polymers; Year 12 starts with biomolecules. Condensation is always one tap away.
 */
export const POLYMER_YEAR_DEFAULTS: readonly { mode: PolymerMode; fromYear: number }[] = [
  { mode: 'addition', fromYear: 11 },
  { mode: 'biomolecules', fromYear: 12 },
];

export const POLYMER_DATA_META = {
  sources: [
    'IUPAC Compendium of Chemical Terminology (Gold Book): addition polymerisation, condensation polymerisation, peptide and glycosidic bond definitions',
    'IUPAC Compendium of Polymer Terminology and Nomenclature (2008): source-based polymer names',
    'IUPAC–IUBMB Joint Commission on Biochemical Nomenclature: amino acid names, three-letter codes and side chains',
    'NIST Chemistry WebBook: molecular formulae of monomers, amino acids, sugars, glycerol and fatty acids',
    'CRC Handbook of Chemistry and Physics: physical state of fats, oils and fatty acids',
  ],
  conditions:
    'Structures and molecular formulae only. Physical states of fats and oils refer to SLC (25 °C, 100 kPa).',
  reviewStatus: 'pending-teaching-review',
} as const;

// ---------------------------------------------------------------------------------------------
// Formula arithmetic
// ---------------------------------------------------------------------------------------------

export type Element = 'C' | 'H' | 'Cl' | 'F' | 'N' | 'O' | 'S';
export type ElementCounts = Partial<Record<Element, number>>;

/** Carbon first, hydrogen second, then the remaining elements alphabetically. */
const FORMULA_ORDER: readonly Element[] = ['C', 'H', 'Cl', 'F', 'N', 'O', 'S'];
const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

export const WATER: ElementCounts = { H: 2, O: 1 };

export function subscriptNumber(value: number): string {
  return String(value)
    .split('')
    .map((digit) => SUBSCRIPT_DIGITS[Number(digit)])
    .join('');
}

export function formatFormula(counts: ElementCounts): string {
  return FORMULA_ORDER.filter((element) => (counts[element] ?? 0) > 0)
    .map((element) => {
      const count = counts[element]!;
      return `${element}${count === 1 ? '' : subscriptNumber(count)}`;
    })
    .join('');
}

export function combineCounts(...parts: Array<{ counts: ElementCounts; times?: number }>) {
  const total: ElementCounts = {};
  for (const { counts, times = 1 } of parts) {
    for (const element of FORMULA_ORDER) {
      const value = (counts[element] ?? 0) * times;
      if (value !== 0) total[element] = (total[element] ?? 0) + value;
    }
  }
  for (const element of FORMULA_ORDER) {
    if (total[element] === 0) delete total[element];
  }
  return total;
}

export function countsEqual(left: ElementCounts, right: ElementCounts): boolean {
  return FORMULA_ORDER.every((element) => (left[element] ?? 0) === (right[element] ?? 0));
}

// ---------------------------------------------------------------------------------------------
// Shared choice questions
// ---------------------------------------------------------------------------------------------

export interface ChoiceOption {
  id: string;
  label: string;
  correct: boolean;
  feedback: string;
}

export interface ChoiceQuestion {
  id: string;
  prompt: string;
  options: ChoiceOption[];
}

export type ChoiceResult =
  | { status: 'correct' | 'incorrect'; message: string }
  | { status: 'unsupported'; message: string };

export function evaluateChoice(question: ChoiceQuestion, optionId: string): ChoiceResult {
  const option = question.options.find((item) => item.id === optionId);
  if (!option) {
    return { status: 'unsupported', message: 'That answer is not one of the reviewed options.' };
  }
  return { status: option.correct ? 'correct' : 'incorrect', message: option.feedback };
}

/** Deterministic rotation so the correct option is not always in the same place. */
function rotate<T>(items: T[], by: number): T[] {
  const shift = by % items.length;
  return [...items.slice(shift), ...items.slice(0, shift)];
}

// ---------------------------------------------------------------------------------------------
// Addition polymers
// ---------------------------------------------------------------------------------------------

export type SideGroupId = 'H' | 'F' | 'Cl' | 'CH3' | 'CN' | 'C6H5';

interface SideGroup {
  label: string;
  counts: ElementCounts;
  /** Single atoms are written inline (CHCl); groups are bracketed (CH(CH₃)). */
  atom: boolean;
}

const SIDE_GROUPS: Record<SideGroupId, SideGroup> = {
  H: { label: 'H', counts: { H: 1 }, atom: true },
  F: { label: 'F', counts: { F: 1 }, atom: true },
  Cl: { label: 'Cl', counts: { Cl: 1 }, atom: true },
  CH3: { label: 'CH₃', counts: { C: 1, H: 3 }, atom: false },
  CN: { label: 'CN', counts: { C: 1, N: 1 }, atom: false },
  C6H5: { label: 'C₆H₅', counts: { C: 6, H: 5 }, atom: false },
};

export interface AdditionMonomer {
  id: string;
  monomerName: string;
  otherName?: string;
  /** Groups on the first and second C=C carbon, drawn [above, below]. */
  carbonA: [SideGroupId, SideGroupId];
  carbonB: [SideGroupId, SideGroupId];
  polymerName: string;
  abbreviation?: string;
  commonUse: string;
  sideGroupDescription: string;
  saturatedName: string;
}

export const ADDITION_MONOMERS: AdditionMonomer[] = [
  {
    id: 'ethene',
    monomerName: 'ethene',
    otherName: 'ethylene',
    carbonA: ['H', 'H'],
    carbonB: ['H', 'H'],
    polymerName: 'polyethene',
    abbreviation: 'PE',
    commonUse: 'plastic bags, cling wrap and milk bottles',
    sideGroupDescription: 'only hydrogen atoms',
    saturatedName: 'ethane',
  },
  {
    id: 'propene',
    monomerName: 'propene',
    otherName: 'propylene',
    carbonA: ['H', 'H'],
    carbonB: ['H', 'CH3'],
    polymerName: 'polypropene',
    abbreviation: 'PP',
    commonUse: 'food containers, bottle caps and ropes',
    sideGroupDescription: 'a –CH₃ side group',
    saturatedName: 'propane',
  },
  {
    id: 'chloroethene',
    monomerName: 'chloroethene',
    otherName: 'vinyl chloride',
    carbonA: ['H', 'H'],
    carbonB: ['H', 'Cl'],
    polymerName: 'poly(chloroethene)',
    abbreviation: 'PVC',
    commonUse: 'water and drain pipes and electrical cable insulation',
    sideGroupDescription: 'a –Cl side group',
    saturatedName: 'chloroethane',
  },
  {
    id: 'phenylethene',
    monomerName: 'phenylethene',
    otherName: 'styrene',
    carbonA: ['H', 'H'],
    carbonB: ['H', 'C6H5'],
    polymerName: 'polystyrene',
    abbreviation: 'PS',
    commonUse: 'foam packaging and disposable cups',
    sideGroupDescription: 'a –C₆H₅ (phenyl) side group',
    saturatedName: 'ethylbenzene',
  },
  {
    id: 'tetrafluoroethene',
    monomerName: 'tetrafluoroethene',
    carbonA: ['F', 'F'],
    carbonB: ['F', 'F'],
    polymerName: 'poly(tetrafluoroethene)',
    abbreviation: 'PTFE',
    commonUse: 'non-stick coatings on frying pans',
    sideGroupDescription: 'four fluorine atoms',
    saturatedName: '1,1,2,2-tetrafluoroethane',
  },
  {
    id: 'propenenitrile',
    monomerName: 'propenenitrile',
    otherName: 'acrylonitrile',
    carbonA: ['H', 'H'],
    carbonB: ['H', 'CN'],
    polymerName: 'polyacrylonitrile',
    abbreviation: 'PAN',
    commonUse: 'acrylic fibres for clothing and carpets',
    sideGroupDescription: 'a –CN (nitrile) side group',
    saturatedName: 'propanenitrile',
  },
];

export function findMonomer(id: string): AdditionMonomer | null {
  return ADDITION_MONOMERS.find((item) => item.id === id) ?? null;
}

function carbonText(groups: SideGroupId[], extraHydrogens = 0): string {
  const hydrogens = groups.filter((group) => group === 'H').length + extraHydrogens;
  const others = groups.filter((group) => group !== 'H');
  const atomCounts = new Map<string, number>();
  const bracketed: string[] = [];
  for (const group of others) {
    const side = SIDE_GROUPS[group];
    if (side.atom) atomCounts.set(side.label, (atomCounts.get(side.label) ?? 0) + 1);
    else bracketed.push(`(${side.label})`);
  }
  const hydrogenText =
    hydrogens === 0 ? '' : hydrogens === 1 ? 'H' : `H${subscriptNumber(hydrogens)}`;
  const atomText = [...atomCounts.entries()]
    .map(([label, count]) => `${label}${count === 1 ? '' : subscriptNumber(count)}`)
    .join('');
  return `C${hydrogenText}${atomText}${bracketed.join('')}`;
}

export function monomerText(monomer: AdditionMonomer): string {
  return `${carbonText(monomer.carbonA)}=${carbonText(monomer.carbonB)}`;
}

export function repeatUnitText(monomer: AdditionMonomer): string {
  return `[–${carbonText(monomer.carbonA)}–${carbonText(monomer.carbonB)}–]ₙ`;
}

/** Misconception: the repeat unit keeps the C=C. Chemically impossible; used as a distractor. */
function doubleBondRepeatText(monomer: AdditionMonomer): string {
  return `[–${carbonText(monomer.carbonA)}=${carbonText(monomer.carbonB)}–]ₙ`;
}

function saturatedText(monomer: AdditionMonomer): string {
  return `${carbonText(monomer.carbonA, 1)}–${carbonText(monomer.carbonB, 1)}`;
}

export function monomerCounts(monomer: AdditionMonomer): ElementCounts {
  return combineCounts(
    { counts: { C: 2 } },
    ...[...monomer.carbonA, ...monomer.carbonB].map((group) => ({
      counts: SIDE_GROUPS[group].counts,
    })),
  );
}

/** An addition repeat unit contains exactly the atoms of one monomer. */
export function repeatUnitCounts(monomer: AdditionMonomer): ElementCounts {
  return monomerCounts(monomer);
}

export interface DiagramAtom {
  id: string;
  label: string;
  x: number;
  y: number;
}

export interface DiagramBond {
  from: string;
  to: string;
  order: 1 | 2;
  /** The bond that changes during polymerisation. */
  highlight?: boolean;
  /** Bond to the next repeat unit; drawn to an unlabelled end point. */
  open?: boolean;
}

export interface StructureDiagram {
  width: number;
  height: number;
  atoms: DiagramAtom[];
  bonds: DiagramBond[];
  brackets: { left: number; right: number; top: number; bottom: number } | null;
  description: string;
}

const DIAGRAM = {
  width: 300,
  height: 180,
  carbonAX: 115,
  carbonBX: 185,
  centreY: 90,
  groupOffsetY: 58,
  openEndLeftX: 35,
  openEndRightX: 265,
  bracketLeftX: 62,
  bracketRightX: 238,
  bracketTopY: 18,
  bracketBottomY: 162,
} as const;

/** Bond-line-style drawing generated from the monomer data, never from artwork. */
export function additionDiagram(
  monomer: AdditionMonomer,
  form: 'monomer' | 'repeat-unit',
): StructureDiagram {
  const top = DIAGRAM.centreY - DIAGRAM.groupOffsetY;
  const bottom = DIAGRAM.centreY + DIAGRAM.groupOffsetY;
  const atoms: DiagramAtom[] = [
    { id: 'C1', label: 'C', x: DIAGRAM.carbonAX, y: DIAGRAM.centreY },
    { id: 'C2', label: 'C', x: DIAGRAM.carbonBX, y: DIAGRAM.centreY },
    { id: 'A1', label: SIDE_GROUPS[monomer.carbonA[0]].label, x: DIAGRAM.carbonAX, y: top },
    { id: 'A2', label: SIDE_GROUPS[monomer.carbonA[1]].label, x: DIAGRAM.carbonAX, y: bottom },
    { id: 'B1', label: SIDE_GROUPS[monomer.carbonB[0]].label, x: DIAGRAM.carbonBX, y: top },
    { id: 'B2', label: SIDE_GROUPS[monomer.carbonB[1]].label, x: DIAGRAM.carbonBX, y: bottom },
  ];
  const bonds: DiagramBond[] = [
    { from: 'C1', to: 'C2', order: form === 'monomer' ? 2 : 1, highlight: true },
    { from: 'C1', to: 'A1', order: 1 },
    { from: 'C1', to: 'A2', order: 1 },
    { from: 'C2', to: 'B1', order: 1 },
    { from: 'C2', to: 'B2', order: 1 },
  ];
  const groups = (pair: SideGroupId[]) =>
    pair.map((group) => SIDE_GROUPS[group].label).join(' and ');
  if (form === 'monomer') {
    return {
      width: DIAGRAM.width,
      height: DIAGRAM.height,
      atoms,
      bonds,
      brackets: null,
      description: `Monomer ${monomer.monomerName}: carbon 1 carries ${groups(monomer.carbonA)}, a double bond joins it to carbon 2, which carries ${groups(monomer.carbonB)}.`,
    };
  }
  atoms.push(
    { id: 'L', label: '', x: DIAGRAM.openEndLeftX, y: DIAGRAM.centreY },
    { id: 'R', label: '', x: DIAGRAM.openEndRightX, y: DIAGRAM.centreY },
  );
  bonds.push(
    { from: 'L', to: 'C1', order: 1, open: true },
    { from: 'C2', to: 'R', order: 1, open: true },
  );
  return {
    width: DIAGRAM.width,
    height: DIAGRAM.height,
    atoms,
    bonds,
    brackets: {
      left: DIAGRAM.bracketLeftX,
      right: DIAGRAM.bracketRightX,
      top: DIAGRAM.bracketTopY,
      bottom: DIAGRAM.bracketBottomY,
    },
    description: `Repeat unit: two carbons joined by a single bond, carrying ${groups(monomer.carbonA)} and ${groups(monomer.carbonB)}, with open bonds to the next units on both sides, repeated n times.`,
  };
}

export type AdditionDirection = 'monomer-to-polymer' | 'polymer-to-monomer';

function polymerLabel(monomer: AdditionMonomer): string {
  return monomer.abbreviation
    ? `${monomer.polymerName} (${monomer.abbreviation})`
    : monomer.polymerName;
}

/** Builds a deterministic four-option question for one monomer, or null for an unknown id. */
export function additionQuestion(
  monomerId: string,
  direction: AdditionDirection,
): ChoiceQuestion | null {
  const index = ADDITION_MONOMERS.findIndex((item) => item.id === monomerId);
  if (index < 0) return null;
  const monomer = ADDITION_MONOMERS[index];
  const others = [1, 2].map(
    (step) => ADDITION_MONOMERS[(index + step) % ADDITION_MONOMERS.length],
  );

  if (direction === 'monomer-to-polymer') {
    const options: ChoiceOption[] = [
      {
        id: `${monomer.id}-repeat`,
        label: repeatUnitText(monomer),
        correct: true,
        feedback: `Correct. One bond of the C=C breaks and each carbon forms a new single bond to the next unit, so ${repeatUnitText(monomer)} keeps every atom of ${monomer.monomerName}. The polymer is ${polymerLabel(monomer)}, used for ${monomer.commonUse}.`,
      },
      {
        id: `${monomer.id}-double`,
        label: doubleBondRepeatText(monomer),
        correct: false,
        feedback:
          'A repeat unit cannot keep C=C. In addition polymerisation one bond of the double bond breaks so each carbon can bond to the next monomer; that carbon would otherwise have five bonds. All other atoms stay where they were.',
      },
      ...others.map((other) => ({
        id: `${other.id}-repeat`,
        label: repeatUnitText(other),
        correct: false,
        feedback: `That repeat unit carries ${other.sideGroupDescription}, so it comes from ${other.monomerName}. Side groups never change during addition polymerisation: ${monomer.monomerName} carries ${monomer.sideGroupDescription}, so it gives ${repeatUnitText(monomer)}.`,
      })),
    ];
    return {
      id: `addition-${monomer.id}-forward`,
      prompt: `Which repeat unit forms when ${monomer.monomerName} (${monomerText(monomer)}) polymerises?`,
      options: rotate(options, index),
    };
  }

  const options: ChoiceOption[] = [
    {
      id: `${monomer.id}-monomer`,
      label: `${monomerText(monomer)} (${monomer.monomerName})`,
      correct: true,
      feedback: `Correct. Remove the open bonds and put the C=C back between the two backbone carbons: ${monomerText(monomer)} is ${monomer.monomerName}. The polymer is ${polymerLabel(monomer)}.`,
    },
    {
      id: `${monomer.id}-saturated`,
      label: `${saturatedText(monomer)} (${monomer.saturatedName})`,
      correct: false,
      feedback: `${monomer.saturatedName} has only single C–C bonds, so it has no double bond to open and cannot join an addition chain. The monomer must be the alkene ${monomer.monomerName}.`,
    },
    ...others.map((other) => ({
      id: `${other.id}-monomer`,
      label: `${monomerText(other)} (${other.monomerName})`,
      correct: false,
      feedback: `${other.monomerName} would give ${repeatUnitText(other)}. Match the side groups: this repeat unit has ${monomer.sideGroupDescription}, which comes from ${monomer.monomerName}.`,
    })),
  ];
  return {
    id: `addition-${monomer.id}-reverse`,
    prompt: `Which monomer made the repeat unit ${repeatUnitText(monomer)}?`,
    options: rotate(options, index + 1),
  };
}

// ---------------------------------------------------------------------------------------------
// Condensation polymers
// ---------------------------------------------------------------------------------------------

export type CondensationLink = 'ester' | 'amide' | 'ether';
export type SmallMolecule = 'H₂O' | 'H₂' | 'CO₂' | 'HCl';

export const CONDENSATION_LINK_OPTIONS: { id: CondensationLink; label: string }[] = [
  { id: 'ester', label: 'Ester –COO–' },
  { id: 'amide', label: 'Amide –CONH–' },
  { id: 'ether', label: 'Ether C–O–C' },
];

export const SMALL_MOLECULE_OPTIONS: SmallMolecule[] = ['H₂O', 'H₂', 'CO₂', 'HCl'];

export interface FormulaSegment {
  text: string;
  /** Marks a newly formed link (ester, amide, peptide or glycosidic). */
  link?: boolean;
}

export interface CondensationMonomer {
  name: string;
  text: string;
  counts: ElementCounts;
  reactingGroup: string;
}

export interface CondensationCase {
  id: string;
  polymerName: string;
  polymerClass: 'polyester' | 'polyamide';
  commonUse: string;
  monomerA: CondensationMonomer;
  monomerB: CondensationMonomer;
  link: Exclude<CondensationLink, 'ether'>;
  eliminated: 'H₂O';
  /** Each repeat unit contains two new links, so two water molecules leave per repeat unit. */
  waterPerRepeatUnit: number;
  repeatUnitText: string;
  repeatUnitCounts: ElementCounts;
  chainSegments: FormulaSegment[];
  /** How the polymer is actually made when that differs from the modelled diacid route. */
  industrialNote?: string;
}

const ETHANEDIOL: CondensationMonomer = {
  name: 'ethane-1,2-diol',
  text: 'HO–CH₂–CH₂–OH',
  counts: { C: 2, H: 6, O: 2 },
  reactingGroup: 'two –OH (hydroxyl) groups',
};

const HEXANEDIAMINE: CondensationMonomer = {
  name: 'hexane-1,6-diamine',
  text: 'H₂N–(CH₂)₆–NH₂',
  counts: { C: 6, H: 16, N: 2 },
  reactingGroup: 'two –NH₂ (amino) groups',
};

const BUTANEDIOL: CondensationMonomer = {
  name: 'butane-1,4-diol',
  text: 'HO–(CH₂)₄–OH',
  counts: { C: 4, H: 10, O: 2 },
  reactingGroup: 'two –OH (hydroxyl) groups',
};

const TEREPHTHALIC_ACID: CondensationMonomer = {
  name: 'benzene-1,4-dicarboxylic acid',
  text: 'HOOC–C₆H₄–COOH',
  counts: { C: 8, H: 6, O: 4 },
  reactingGroup: 'two –COOH (carboxyl) groups',
};

const BUTANEDIOIC_ACID: CondensationMonomer = {
  name: 'butanedioic acid',
  text: 'HOOC–CH₂–CH₂–COOH',
  counts: { C: 4, H: 6, O: 4 },
  reactingGroup: 'two –COOH (carboxyl) groups',
};

const HEXANEDIOIC_ACID: CondensationMonomer = {
  name: 'hexanedioic acid',
  text: 'HOOC–(CH₂)₄–COOH',
  counts: { C: 6, H: 10, O: 4 },
  reactingGroup: 'two –COOH (carboxyl) groups',
};

const WATER_PER_REPEAT_UNIT = 2;

export const CONDENSATION_CASES: CondensationCase[] = [
  {
    id: 'pet',
    polymerName: 'PET, poly(ethylene terephthalate)',
    polymerClass: 'polyester',
    commonUse: 'soft-drink bottles and polyester clothing fibres',
    monomerA: ETHANEDIOL,
    monomerB: TEREPHTHALIC_ACID,
    link: 'ester',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–O–CH₂–CH₂–O–CO–C₆H₄–CO–]ₙ',
    repeatUnitCounts: { C: 10, H: 8, O: 4 },
    chainSegments: [
      { text: '⋯–C₆H₄–' },
      { text: 'CO–O', link: true },
      { text: '–CH₂–CH₂–' },
      { text: 'O–CO', link: true },
      { text: '–C₆H₄–' },
      { text: 'CO–O', link: true },
      { text: '–CH₂–⋯' },
    ],
  },
  {
    id: 'pes',
    polymerName: 'poly(ethylene succinate)',
    polymerClass: 'polyester',
    commonUse: 'research into biodegradable packaging plastics',
    monomerA: ETHANEDIOL,
    monomerB: BUTANEDIOIC_ACID,
    link: 'ester',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–O–CH₂–CH₂–O–CO–CH₂–CH₂–CO–]ₙ',
    repeatUnitCounts: { C: 6, H: 8, O: 4 },
    chainSegments: [
      { text: '⋯–CH₂–' },
      { text: 'CO–O', link: true },
      { text: '–CH₂–CH₂–' },
      { text: 'O–CO', link: true },
      { text: '–CH₂–CH₂–' },
      { text: 'CO–O', link: true },
      { text: '–CH₂–⋯' },
    ],
  },
  {
    id: 'pbt',
    polymerName: 'PBT, poly(butylene terephthalate)',
    polymerClass: 'polyester',
    commonUse: 'electrical connectors and moulded car parts',
    monomerA: BUTANEDIOL,
    monomerB: TEREPHTHALIC_ACID,
    link: 'ester',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–O–(CH₂)₄–O–CO–C₆H₄–CO–]ₙ',
    repeatUnitCounts: { C: 12, H: 12, O: 4 },
    chainSegments: [
      { text: '⋯–C₆H₄–' },
      { text: 'CO–O', link: true },
      { text: '–(CH₂)₄–' },
      { text: 'O–CO', link: true },
      { text: '–C₆H₄–' },
      { text: 'CO–O', link: true },
      { text: '–(CH₂)₄–⋯' },
    ],
  },
  {
    id: 'ptt',
    polymerName: 'PTT, poly(trimethylene terephthalate)',
    polymerClass: 'polyester',
    commonUse: 'carpet and stretch clothing fibres',
    monomerA: {
      name: 'propane-1,3-diol',
      text: 'HO–(CH₂)₃–OH',
      counts: { C: 3, H: 8, O: 2 },
      reactingGroup: 'two –OH (hydroxyl) groups',
    },
    monomerB: TEREPHTHALIC_ACID,
    link: 'ester',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–O–(CH₂)₃–O–CO–C₆H₄–CO–]ₙ',
    repeatUnitCounts: { C: 11, H: 10, O: 4 },
    chainSegments: [
      { text: '⋯–C₆H₄–' },
      { text: 'CO–O', link: true },
      { text: '–(CH₂)₃–' },
      { text: 'O–CO', link: true },
      { text: '–C₆H₄–' },
      { text: 'CO–O', link: true },
      { text: '–(CH₂)₃–⋯' },
    ],
  },
  {
    id: 'pbs',
    polymerName: 'PBS, poly(butylene succinate)',
    polymerClass: 'polyester',
    commonUse: 'biodegradable mulch film and compostable packaging',
    monomerA: BUTANEDIOL,
    monomerB: BUTANEDIOIC_ACID,
    link: 'ester',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–O–(CH₂)₄–O–CO–CH₂–CH₂–CO–]ₙ',
    repeatUnitCounts: { C: 8, H: 12, O: 4 },
    chainSegments: [
      { text: '⋯–CH₂–' },
      { text: 'CO–O', link: true },
      { text: '–(CH₂)₄–' },
      { text: 'O–CO', link: true },
      { text: '–CH₂–CH₂–' },
      { text: 'CO–O', link: true },
      { text: '–(CH₂)₄–⋯' },
    ],
  },
  {
    id: 'nylon-6-6',
    polymerName: 'nylon-6,6',
    polymerClass: 'polyamide',
    commonUse: 'clothing fibres, ropes and carpets',
    monomerA: HEXANEDIAMINE,
    monomerB: HEXANEDIOIC_ACID,
    link: 'amide',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–NH–(CH₂)₆–NH–CO–(CH₂)₄–CO–]ₙ',
    repeatUnitCounts: { C: 12, H: 22, N: 2, O: 2 },
    chainSegments: [
      { text: '⋯–(CH₂)₄–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₆–' },
      { text: 'NH–CO', link: true },
      { text: '–(CH₂)₄–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₆–⋯' },
    ],
  },
  {
    id: 'nylon-6-10',
    polymerName: 'nylon-6,10',
    polymerClass: 'polyamide',
    commonUse: 'toothbrush bristles and other brush filaments',
    monomerA: HEXANEDIAMINE,
    monomerB: {
      name: 'decanedioic acid',
      text: 'HOOC–(CH₂)₈–COOH',
      counts: { C: 10, H: 18, O: 4 },
      reactingGroup: 'two –COOH (carboxyl) groups',
    },
    link: 'amide',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–NH–(CH₂)₆–NH–CO–(CH₂)₈–CO–]ₙ',
    repeatUnitCounts: { C: 16, H: 30, N: 2, O: 2 },
    chainSegments: [
      { text: '⋯–(CH₂)₈–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₆–' },
      { text: 'NH–CO', link: true },
      { text: '–(CH₂)₈–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₆–⋯' },
    ],
  },
  {
    id: 'nylon-4-6',
    polymerName: 'nylon-4,6',
    polymerClass: 'polyamide',
    commonUse: 'gears and engine parts that must stay strong when hot',
    monomerA: {
      name: 'butane-1,4-diamine',
      text: 'H₂N–(CH₂)₄–NH₂',
      counts: { C: 4, H: 12, N: 2 },
      reactingGroup: 'two –NH₂ (amino) groups',
    },
    monomerB: HEXANEDIOIC_ACID,
    link: 'amide',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–NH–(CH₂)₄–NH–CO–(CH₂)₄–CO–]ₙ',
    repeatUnitCounts: { C: 10, H: 18, N: 2, O: 2 },
    chainSegments: [
      { text: '⋯–(CH₂)₄–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₄–' },
      { text: 'NH–CO', link: true },
      { text: '–(CH₂)₄–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₄–⋯' },
    ],
  },
  {
    id: 'nylon-6-12',
    polymerName: 'nylon-6,12',
    polymerClass: 'polyamide',
    commonUse: 'fuel lines, tubing and brush filaments that absorb little water',
    monomerA: HEXANEDIAMINE,
    monomerB: {
      name: 'dodecanedioic acid',
      text: 'HOOC–(CH₂)₁₀–COOH',
      counts: { C: 12, H: 22, O: 4 },
      reactingGroup: 'two –COOH (carboxyl) groups',
    },
    link: 'amide',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–NH–(CH₂)₆–NH–CO–(CH₂)₁₀–CO–]ₙ',
    repeatUnitCounts: { C: 18, H: 34, N: 2, O: 2 },
    chainSegments: [
      { text: '⋯–(CH₂)₁₀–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₆–' },
      { text: 'NH–CO', link: true },
      { text: '–(CH₂)₁₀–' },
      { text: 'CO–NH', link: true },
      { text: '–(CH₂)₆–⋯' },
    ],
  },
  {
    id: 'kevlar',
    polymerName: 'Kevlar (an aramid)',
    polymerClass: 'polyamide',
    commonUse: 'body armour, helmets and cut-resistant gloves',
    monomerA: {
      name: 'benzene-1,4-diamine',
      text: 'H₂N–C₆H₄–NH₂',
      counts: { C: 6, H: 8, N: 2 },
      reactingGroup: 'two –NH₂ (amino) groups',
    },
    monomerB: TEREPHTHALIC_ACID,
    link: 'amide',
    eliminated: 'H₂O',
    waterPerRepeatUnit: WATER_PER_REPEAT_UNIT,
    repeatUnitText: '[–NH–C₆H₄–NH–CO–C₆H₄–CO–]ₙ',
    repeatUnitCounts: { C: 14, H: 10, N: 2, O: 2 },
    chainSegments: [
      { text: '⋯–C₆H₄–' },
      { text: 'CO–NH', link: true },
      { text: '–C₆H₄–' },
      { text: 'NH–CO', link: true },
      { text: '–C₆H₄–' },
      { text: 'CO–NH', link: true },
      { text: '–C₆H₄–⋯' },
    ],
    industrialNote:
      'This model uses the diacid route so that water is eliminated. Industry makes Kevlar from benzene-1,4-dicarbonyl dichloride (the acid chloride, ClOC–C₆H₄–COCl) instead, and that route eliminates HCl, not H₂O. The repeat unit is the same.',
  },
];

export interface CondensationAnswer {
  link: CondensationLink;
  molecule: SmallMolecule;
}

export type CondensationResult =
  | {
      status: 'checked';
      linkCorrect: boolean;
      moleculeCorrect: boolean;
      linkFeedback: string;
      moleculeFeedback: string;
    }
  | { status: 'unsupported'; message: string };

const LINK_TEXT: Record<CondensationLink, string> = {
  ester: 'an ester –COO–',
  amide: 'an amide –CONH–',
  ether: 'an ether C–O–C',
};

function linkFeedback(item: CondensationCase, chosen: CondensationLink): string {
  const partner = item.monomerA.reactingGroup;
  if (chosen === item.link) {
    return `Correct: each –COOH group reacts with one of the ${partner}, so every new link is ${LINK_TEXT[item.link]}. That makes ${item.polymerName} a ${item.polymerClass}.`;
  }
  if (chosen === 'ether') {
    return `An ether link would leave no C=O between the units. The carboxyl carbon keeps its C=O, so the link is ${LINK_TEXT[item.link]}.`;
  }
  if (chosen === 'amide') {
    return `An amide link needs nitrogen from an –NH₂ group. ${item.monomerA.name} supplies –OH groups instead, so the link is ${LINK_TEXT.ester}.`;
  }
  return `An ester link needs an alcohol –OH group. ${item.monomerA.name} supplies –NH₂ groups instead, so the link is ${LINK_TEXT.amide}.`;
}

function moleculeFeedback(item: CondensationCase, chosen: SmallMolecule): string {
  const hydrogenSource = item.link === 'ester' ? '–OH' : '–NH₂';
  if (chosen === 'H₂O') {
    return `Correct: OH from each –COOH and H from each ${hydrogenSource} leave as water. Each repeat unit forms ${item.waterPerRepeatUnit} links, so ${item.waterPerRepeatUnit} H₂O leave per repeat unit.`;
  }
  if (chosen === 'H₂') {
    return 'No H₂ forms. The –OH of the carboxyl group must leave as well as a hydrogen atom, so the small molecule is H₂O.';
  }
  if (chosen === 'CO₂') {
    return 'CO₂ would remove carbon from the chain. Condensation keeps every carbon atom; only H and OH leave, together as H₂O.';
  }
  return 'HCl forms only when an acid chloride (–COCl) is used instead of the acid. These monomers are carboxylic acids, so the small molecule is H₂O.';
}

export function evaluateCondensation(caseId: string, answer: CondensationAnswer): CondensationResult {
  const item = CONDENSATION_CASES.find((entry) => entry.id === caseId);
  const linkKnown = CONDENSATION_LINK_OPTIONS.some((option) => option.id === answer.link);
  if (!item || !linkKnown || !SMALL_MOLECULE_OPTIONS.includes(answer.molecule)) {
    return {
      status: 'unsupported',
      message: 'That polymer, link or small molecule is not in the reviewed set.',
    };
  }
  return {
    status: 'checked',
    linkCorrect: answer.link === item.link,
    moleculeCorrect: answer.molecule === item.eliminated,
    linkFeedback: linkFeedback(item, answer.link),
    moleculeFeedback: moleculeFeedback(item, answer.molecule),
  };
}
